import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { ButtonDirective } from '../spartan/button.directive';
import {
  SelectContentComponent,
  SelectDirective,
  SelectItemDirective,
  SelectPortalDirective,
  SelectTriggerComponent,
  SelectValueDirective,
} from '../spartan/select.directive';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [
    ButtonDirective,
    SelectDirective,
    SelectTriggerComponent,
    SelectValueDirective,
    SelectPortalDirective,
    SelectContentComponent,
    SelectItemDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav
      class="flex items-center justify-between gap-3 border-t border-border pt-3"
      aria-label="Paginación de resultados"
    >
      <p class="m-0 text-sm text-muted">{{ rangeLabel() }}</p>

      <div class="flex items-center gap-1">
        <button
          appButton
          type="button"
          variant="outline"
          size="sm"
          class="w-8 px-0"
          aria-label="Página anterior"
          [disabled]="isFirst()"
          (click)="goTo(page() - 1)"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>

        @for (pageNumber of visiblePages(); track pageNumber) {
          <button
            appButton
            type="button"
            size="sm"
            [variant]="pageNumber === page() ? 'default' : 'outline'"
            [attr.aria-current]="pageNumber === page() ? 'page' : null"
            [disabled]="disabled()"
            (click)="goTo(pageNumber)"
          >
            {{ pageNumber + 1 }}
          </button>
        }

        <button
          appButton
          type="button"
          variant="outline"
          size="sm"
          class="w-8 px-0"
          aria-label="Página siguiente"
          [disabled]="isLast()"
          (click)="goTo(page() + 1)"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
        <div
          appSelect
          [value]="size()"
          [disabled]="disabled()"
          (valueChange)="onSizeChange($any($event))"
          class="w-16"
        >
          <app-select-trigger>
            <span appSelectValue></span>
          </app-select-trigger>
          <app-select-content *appSelectPortal>
            @for (option of pageSizeOptions(); track option) {
              <div appSelectItem [value]="option">{{ option }}</div>
            }
          </app-select-content>
        </div>
      </div>
    </nav>
  `,
})
export class PaginationComponent {
  readonly page = input.required<number>();
  readonly size = input.required<number>();
  readonly totalPages = input.required<number>();
  readonly totalElements = input.required<number>();
  readonly disabled = input(false);
  readonly window = input(5);
  readonly pageSizeOptions = input<readonly number[]>(DEFAULT_PAGE_SIZE_OPTIONS);

  readonly pageChange = output<number>();
  readonly sizeChange = output<number>();

  protected readonly isFirst = computed(() => this.disabled() || this.page() <= 0);
  protected readonly isLast = computed(
    () => this.disabled() || this.page() >= this.totalPages() - 1,
  );

  protected readonly rangeLabel = computed(() => {
    const total = this.totalElements();
    if (total === 0) return 'Sin resultados';
    const from = this.page() * this.size() + 1;
    const to = Math.min(from + this.size() - 1, total);
    return `Mostrando ${from}-${to} de ${total}`;
  });

  protected readonly visiblePages = computed<number[]>(() => {
    const total = Math.max(this.totalPages(), 1);
    const span = Math.min(this.window(), total);
    const start = Math.min(Math.max(this.page() - Math.floor(span / 2), 0), total - span);
    return Array.from({ length: span }, (_, index) => start + index);
  });

  protected goTo(target: number): void {
    if (this.disabled() || target < 0 || target > this.totalPages() - 1 || target === this.page()) {
      return;
    }
    this.pageChange.emit(target);
  }

  protected onSizeChange(size: number): void {
    if (size !== this.size()) {
      this.sizeChange.emit(size);
    }
  }
}
